/**
 * Build fingerprint + gameplay-critical mismatch detection. (§12.6, §15.2)
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 *
 * PROTOCOL_VERSION guards the BYTES on the wire: change the layout, bump it, and
 * a client refuses a server that would misread the frames. But two peers can
 * agree on every byte and still be a different GAME — a tuning change to
 * constants.ts (BALL_RESTITUTION_GRASS, a pass speed, a turn rate) leaves the
 * wire untouched yet makes the sim produce a different outcome from the same
 * (state, inputs). §15.2 is unforgiving about this: if the client and server run
 * different sim code the game desyncs, and the symptom (players juddering,
 * reconciliation fighting every snapshot) points nowhere near the cause. A
 * protocol bump for every tuning tweak would be absurd (football feel is 200
 * iterations on 50 numbers — §15.5), so the fingerprint splits the two concerns:
 *
 *   protocolVersion    the wire format (bump = re-deploy both sides)
 *   simulationVersion  sim BEHAVIOUR in the code that no constant reflects
 *   constantsHash      a deterministic hash of every gameplay tunable — flips
 *                      the instant any number in constants.ts changes, WITHOUT a
 *                      protocol bump
 *   gitCommit          pure build metadata — NEVER gates play (two builds off
 *                      different commits with identical sim are compatible)
 *
 * ── Purity ──────────────────────────────────────────────────────────────────
 *
 * This file is NOT a /sim file, so the sim-lint bans (no Date.now/Math.random/
 * performance.now) do not apply to it — but the hash is written to be pure and
 * deterministic anyway, because both the client and the server compute it
 * independently and must agree bit-for-bit. It reads only the constants module
 * (numbers/booleans/plain objects) and uses only integer arithmetic + string
 * iteration (FNV-1a). gitCommit is the one impure read, and it is deliberately
 * excluded from every compatibility decision — it is boot metadata, not sim
 * state, and it never enters the authoritative tick.
 */

import * as SIM_CONSTANTS from '../sim/constants.js';
import { PROTOCOL_VERSION, SIMULATION_VERSION } from '../sim/constants.js';

/**
 * Identifies exactly which build a peer is running. Carried in the Welcome so a
 * client can compare the server's against its own before it trusts a single tick.
 */
export interface BuildFingerprint {
  /** Short git commit of the build, or 'dev'. Build metadata — never gates play. */
  gitCommit: string;
  /** Wire-format version (constants.ts PROTOCOL_VERSION). */
  protocolVersion: number;
  /** Sim-behaviour version (constants.ts SIMULATION_VERSION). */
  simulationVersion: number;
  /** Deterministic hash of every gameplay tunable — see below. */
  constantsHash: string;
}

/** Why two fingerprints are incompatible. `null` (from classify) = compatible. */
export type FingerprintMismatchReason = 'protocol' | 'simulation' | 'constants' | 'missing';

// ---------------------------------------------------------------------------
// constantsHash — FNV-1a hex over a canonical, ordered serialization of every
// numeric/boolean gameplay tunable in constants.ts.
// ---------------------------------------------------------------------------

const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

/**
 * Excluded from the constants hash: the two VERSION integers themselves. They are
 * compared as their own fingerprint fields, so folding them into the hash too
 * would only make a version bump ALSO read as a "constants" mismatch — noise. The
 * hash's job is orthogonal: it changes iff a GAMEPLAY NUMBER changed.
 */
const HASH_EXCLUDE: ReadonlySet<string> = new Set(['PROTOCOL_VERSION', 'SIMULATION_VERSION']);

/** FNV-1a over the char codes of `s` (two bytes per char; all inputs are ASCII,
 *  but the high byte is mixed anyway so a stray non-ASCII name cannot collide). */
function fnv1aHex(s: string): string {
  let h = FNV_OFFSET_BASIS;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h ^= c & 0xff;
    h = Math.imul(h, FNV_PRIME);
    h ^= (c >>> 8) & 0xff;
    h = Math.imul(h, FNV_PRIME);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

/**
 * Canonical string form of one exported value. Numbers use the spec-defined
 * shortest round-trip form (Number::toString, identical on every V8), booleans
 * and strings are literal, plain objects (SKILL_DURATIONS_S, TUNABLES) recurse
 * with SORTED keys so the string is order-independent. Anything else (functions,
 * undefined) contributes nothing.
 */
function serializeValue(v: unknown): string {
  if (typeof v === 'number') return numberToCanonical(v);
  if (typeof v === 'boolean') return v ? 'T' : 'F';
  if (typeof v === 'string') return `"${v}"`;
  if (v !== null && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    const keys = Object.keys(o).sort();
    let s = '{';
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i]!;
      s += `${k}:${serializeValue(o[k])},`;
    }
    return `${s}}`;
  }
  return '';
}

/** -0 → "0" already via String; keep NaN/Infinity legible though none occur. */
function numberToCanonical(n: number): string {
  if (n === 0) return '0'; // collapse -0
  return String(n);
}

/**
 * Build the canonical serialization once, over ALL exports of constants.ts in
 * SORTED name order. Iterating the whole module (rather than a hand-kept list)
 * means a newly-added gameplay constant is folded in automatically — the hash
 * cannot silently miss a tunable someone forgot to register.
 */
function computeConstantsHash(): string {
  const source = SIM_CONSTANTS as unknown as Record<string, unknown>;
  const names = Object.keys(source)
    .filter((k) => !HASH_EXCLUDE.has(k))
    .sort();
  let canonical = '';
  for (let i = 0; i < names.length; i++) {
    const name = names[i]!;
    const value = source[name];
    // Only value-bearing tunables (number/boolean/string/plain object). A
    // function export or a type would serialize to '' and add nothing.
    if (
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      typeof value === 'string' ||
      (value !== null && typeof value === 'object')
    ) {
      canonical += `${name}=${serializeValue(value)};`;
    }
  }
  return fnv1aHex(canonical);
}

/** Computed once at module load — pure, so it is a module constant, not per-call. */
export const CONSTANTS_HASH: string = computeConstantsHash();

// ---------------------------------------------------------------------------
// gitCommit — build metadata, read once at boot. NEVER enters the sim tick.
// ---------------------------------------------------------------------------

/**
 * Resolve the git commit from the environment. The server passes FG_GIT_COMMIT
 * (set by CI, or `git rev-parse --short HEAD` at boot — see the server entry
 * point); the browser has no process env, so the client reads 'dev' unless a
 * build define set globalThis.FG_GIT_COMMIT. Guarded so touching `process` in a
 * browser bundle can never throw.
 */
function resolveGitCommit(): string {
  try {
    const g = globalThis as unknown as { FG_GIT_COMMIT?: unknown };
    if (typeof g.FG_GIT_COMMIT === 'string' && g.FG_GIT_COMMIT.length > 0) {
      return g.FG_GIT_COMMIT;
    }
    const proc = (globalThis as unknown as { process?: { env?: Record<string, string | undefined> } })
      .process;
    const fromEnv = proc?.env?.FG_GIT_COMMIT;
    if (typeof fromEnv === 'string' && fromEnv.length > 0) return fromEnv;
  } catch {
    /* no process/globalThis env — fall through */
  }
  return 'dev';
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** The full fingerprint of THIS build. gitCommit is read at call time (boot). */
export function buildFingerprint(): BuildFingerprint {
  return {
    gitCommit: resolveGitCommit(),
    protocolVersion: PROTOCOL_VERSION,
    simulationVersion: SIMULATION_VERSION,
    constantsHash: CONSTANTS_HASH,
  };
}

/**
 * True when a and b would run a DIFFERENT sim outcome: protocolVersion,
 * simulationVersion or constantsHash differ. gitCommit is deliberately ignored —
 * two builds off different commits with identical sim are compatible (§12.6).
 */
export function gameplayCriticalMismatch(a: BuildFingerprint, b: BuildFingerprint): boolean {
  return (
    a.protocolVersion !== b.protocolVersion ||
    a.simulationVersion !== b.simulationVersion ||
    a.constantsHash !== b.constantsHash
  );
}

/**
 * Classify why a server fingerprint is incompatible with the local one, for a
 * visible rejection and for tests. Returns `null` when compatible.
 *
 * `requireFingerprint` (production): a server that sent NO fingerprint (an empty
 * constantsHash / absent simulationVersion — an old or malformed server) is
 * rejected outright as 'missing'. In dev the missing case is tolerated so a
 * mixed local stack still connects, but the protocol gate is still enforced.
 */
export function classifyFingerprintMismatch(
  local: BuildFingerprint,
  server: Partial<BuildFingerprint> | null | undefined,
  opts: { requireFingerprint: boolean },
): FingerprintMismatchReason | null {
  const present =
    server != null &&
    typeof server.constantsHash === 'string' &&
    server.constantsHash.length > 0 &&
    typeof server.simulationVersion === 'number';

  if (!present) {
    if (opts.requireFingerprint) return 'missing';
    // Dev tolerance: an old server without a fingerprint can still be caught by
    // the protocol gate, which every Welcome carries.
    if (server != null && server.protocolVersion !== local.protocolVersion) return 'protocol';
    return null;
  }

  if (server.protocolVersion !== local.protocolVersion) return 'protocol';
  if (server.simulationVersion !== local.simulationVersion) return 'simulation';
  if (server.constantsHash !== local.constantsHash) return 'constants';
  return null;
}

/** Compact one-line readout for the HUD / debug overlay: `dev · p3 · s1 · a1b2c3d4`. */
export function formatFingerprint(fp: BuildFingerprint): string {
  const hash = fp.constantsHash.length > 8 ? fp.constantsHash.slice(0, 8) : fp.constantsHash;
  return `${fp.gitCommit} · p${fp.protocolVersion} · s${fp.simulationVersion} · ${hash}`;
}

/** Human-readable one-liner for a rejection reason, for a visible client error. */
export function mismatchMessage(
  reason: FingerprintMismatchReason,
  local: BuildFingerprint,
  server: Partial<BuildFingerprint> | null | undefined,
): string {
  switch (reason) {
    case 'protocol':
      return `Version mismatch: protocol (server ${server?.protocolVersion ?? '?'}, client ${local.protocolVersion}). Refresh.`;
    case 'simulation':
      return `Version mismatch: sim behaviour (server ${server?.simulationVersion ?? '?'}, client ${local.simulationVersion}). Refresh.`;
    case 'constants':
      return `Version mismatch: gameplay tuning (server ${server?.constantsHash ?? '?'}, client ${local.constantsHash}). Refresh.`;
    case 'missing':
      return 'Server sent no build fingerprint — refusing to play (production requires it). Refresh.';
    default:
      return 'Version mismatch. Refresh.';
  }
}
